import { useState } from "react";
import { useSettings } from "../store/settings";
import { useClientBrand, effectiveBrand } from "../store/clientBrand";
import { CLIENTS, clientById } from "../data/clients";
import { generateFunnelAI, type FunnelSpec, type FunnelNode } from "../lib/claude";

const STAGES: { id: FunnelNode["stage"]; label: string; color: string; icon: string }[] = [
  { id: "awareness",     label: "TOPO · Awareness",          color: "#BC2A24", icon: "🎯" },
  { id: "interest",      label: "INTERESSE · Captura",       color: "#A8863C", icon: "🪝" },
  { id: "consideration", label: "CONSIDERAÇÃO · Nutrição",   color: "#3C3A36", icon: "💭" },
  { id: "decision",      label: "DECISÃO · Fundo",           color: "#121214", icon: "🎯" },
  { id: "retention",     label: "RETENÇÃO · Pós-compra",     color: "#5A5A5C", icon: "🤝" },
  { id: "advocacy",      label: "ADVOCACIA · Indicação",     color: "#A8863C", icon: "📣" }
];

export function FunnelView() {
  const settings = useSettings();
  const overrides = useClientBrand(s => s.overrides);

  const [brief, setBrief] = useState("");
  const [clientId, setClientId] = useState("");
  const [goal, setGoal] = useState("");
  const [voice, setVoice] = useState<"brasao" | "thiagoroas" | "jebblount" | "balanced">("balanced");
  const [busy, setBusy] = useState(false);
  const [funnel, setFunnel] = useState<FunnelSpec | null>(null);
  const [openNode, setOpenNode] = useState<FunnelNode | null>(null);

  const generate = async () => {
    if (!brief.trim()) { alert("Descreva o que vai virar funil."); return; }
    if (!settings.anthropicKey) { alert("Configure chave Anthropic em ⚙️."); return; }
    setBusy(true);
    try {
      const cli = clientId ? clientById(clientId) : undefined;
      const eff = cli ? effectiveBrand(cli, overrides[clientId]) : undefined;
      const out = await generateFunnelAI({
        apiKey: settings.anthropicKey, model: settings.model,
        brief, goal: goal || undefined, voice,
        clientName: cli?.name, clientPitch: eff?.pitch,
        audience: eff?.audience ?? cli?.audience
      });
      setFunnel(out);
    } catch (e) {
      alert("Falha: " + (e as Error).message);
    } finally { setBusy(false); }
  };

  const byStage = (s: FunnelNode["stage"]) => funnel?.nodes.filter(n => n.stage === s) ?? [];

  return (
    <div className="px-8 pb-16">
      <header className="border-2 border-ink bg-ink text-bone px-5 py-4 mb-4">
        <div className="font-mono text-xs text-gold">FUN-01</div>
        <h2 className="font-display font-black uppercase text-4xl leading-none">FUNIL DE VENDAS</h2>
        <div className="font-serif italic mt-1 text-bone/70">
          mapa mental gerado pela IA · Brasão · Thiago Roas · Jeb Blount
        </div>
      </header>

      {/* INPUT */}
      <section className="border-2 border-ink bg-bone p-4 mb-4 space-y-3">
        <div>
          <div className="tag text-graydk mb-1">briefing — o que vai virar funil</div>
          <textarea value={brief} onChange={(e) => setBrief(e.target.value)}
            rows={3}
            placeholder="ex: vender Mentoria VPC (R$ 5.000) pra empresários 7 dígitos · investimento de R$ 50k em paid em 30 dias"
            className="w-full border border-ink bg-bone px-3 py-2 font-mono text-[13px]" />
        </div>
        <div className="grid md:grid-cols-3 gap-2">
          <div>
            <div className="tag text-graydk mb-1">cliente</div>
            <select value={clientId} onChange={(e) => setClientId(e.target.value)}
              className="w-full border border-ink bg-bone px-2 py-1 font-mono text-[12px]">
              <option value="">— interno —</option>
              {CLIENTS.map(c => <option key={c.id} value={c.id}>{c.shortName}</option>)}
            </select>
          </div>
          <div>
            <div className="tag text-graydk mb-1">objetivo principal</div>
            <input value={goal} onChange={(e) => setGoal(e.target.value)}
              placeholder="ex: 20 matrículas/mês a R$5k"
              className="w-full border border-ink bg-bone px-2 py-1 font-mono text-[12px]" />
          </div>
          <div>
            <div className="tag text-graydk mb-1">voz do funil</div>
            <select value={voice} onChange={(e) => setVoice(e.target.value as any)}
              className="w-full border border-ink bg-bone px-2 py-1 font-mono text-[12px]">
              <option value="balanced">balanceado (3 misturados)</option>
              <option value="brasao">Brasão · funnel hacker BR</option>
              <option value="thiagoroas">Thiago Roas · ROAS engineer</option>
              <option value="jebblount">Jeb Blount · outbound B2B</option>
            </select>
          </div>
        </div>
        <button onClick={generate} disabled={busy}
          className="btn bg-red text-bone border-red py-3 text-base w-full disabled:opacity-50">
          {busy ? "🧠 montando funil…" : "✦ GERAR FUNIL DE VENDAS"}
        </button>
      </section>

      {funnel && (
        <>
          {/* HEADER do funil */}
          <section className="border-2 border-ink bg-gold/10 p-4 mb-4">
            <div className="font-display font-black uppercase text-2xl">{funnel.title}</div>
            <p className="font-crimson italic text-paper mt-1">{funnel.description}</p>
            <div className="grid md:grid-cols-2 gap-2 mt-3 text-[12px] font-mono">
              <div><span className="tag text-graydk">objetivo</span> {funnel.goal}</div>
              <div><span className="tag text-graydk">métrica principal</span> {funnel.primaryMetric}</div>
            </div>
          </section>

          {/* MAPA MENTAL (funil em forma de funil literal) */}
          <section className="border-2 border-ink bg-bone p-3">
            <div className="space-y-3">
              {STAGES.map((stage, stageIdx) => {
                const nodes = byStage(stage.id);
                if (nodes.length === 0) return null;
                // largura proporcional pra criar visual de funil afunilando
                const widthPct = 100 - stageIdx * 12;
                return (
                  <div key={stage.id} className="flex justify-center">
                    <div style={{ width: `${widthPct}%` }} className="border-2 border-ink overflow-hidden">
                      <header className="px-3 py-1.5 flex items-center justify-between"
                              style={{ background: stage.color, color: stage.color === "#A8863C" ? "#121214" : "#F2EEE6" }}>
                        <span className="font-display font-bold uppercase text-sm">
                          {stage.icon} {stage.label}
                        </span>
                        <span className="tag opacity-70">{nodes.length} {nodes.length === 1 ? "nó" : "nós"}</span>
                      </header>
                      <div className="p-2 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-1.5">
                        {nodes.map(n => (
                          <FunnelNodeCard key={n.id} node={n} onOpen={() => setOpenNode(n)} />
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        </>
      )}

      {openNode && <NodeDetailModal node={openNode} onClose={() => setOpenNode(null)} />}
    </div>
  );
}

function FunnelNodeCard({ node, onOpen }: { node: FunnelNode; onOpen: () => void }) {
  return (
    <button onClick={onOpen}
      className="text-left border border-ink bg-bone p-2 hover:bg-ink/5 transition">
      <div className="font-display font-bold uppercase text-[11px] leading-tight line-clamp-2">
        {node.title}
      </div>
      {node.channel && (
        <div className="tag text-graydk mt-0.5">{node.channel}</div>
      )}
      {node.metric && (
        <div className="font-mono text-[10px] text-red mt-0.5">{node.metric}</div>
      )}
    </button>
  );
}

function NodeDetailModal({ node, onClose }: { node: FunnelNode; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 bg-ink/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-bone border-2 border-ink w-full max-w-2xl shadow-[6px_6px_0_0_#121214]"
           onClick={(e) => e.stopPropagation()}>
        <header className="bg-ink text-bone px-5 py-3 flex items-center justify-between">
          <div>
            <div className="font-mono text-xs text-gold">{node.stage.toUpperCase()}</div>
            <h2 className="font-display font-black uppercase text-2xl leading-none">{node.title}</h2>
          </div>
          <button onClick={onClose} className="btn bg-bone text-ink border-bone">×</button>
        </header>
        <div className="p-5 space-y-3 font-mono text-[13px]">
          {node.channel && (<Row label="canal" v={node.channel} />)}
          {node.hook && (<Row label="hook" v={node.hook} />)}
          {node.cta && (<Row label="CTA" v={node.cta} />)}
          {node.metric && (<Row label="métrica" v={node.metric} />)}
          {node.notes && (<Row label="notas" v={node.notes} />)}
        </div>
      </div>
    </div>
  );
}

function Row({ label, v }: { label: string; v: string }) {
  return (
    <div>
      <div className="tag text-graydk mb-1">{label}</div>
      <div className="bg-ink/5 border border-ink/20 px-3 py-2">{v}</div>
    </div>
  );
}
